import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Calculator, TrendingDown, ExternalLink, BookOpen, Share2, Check, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";

interface ToolPricing {
  name: string;
  logo: string;
  url: string;
  calculate: (users: number) => number;
  note: string;
  isRecommended?: boolean;
}

const toolPricing: ToolPricing[] = [
  {
    name: "Lottae",
    logo: "🏆",
    url: "https://www.lottae.com/pricing",
    calculate: (users: number) => 7 + (users * 3.5),
    note: "All features included",
    isRecommended: true,
  },
  {
    name: "Asana",
    logo: "📋",
    url: "https://asana.com",
    calculate: (users: number) => users <= 10 ? 0 : users * 10.99,
    note: "Premium tier",
  },
  {
    name: "Monday.com",
    logo: "📊",
    url: "https://monday.com",
    calculate: (users: number) => Math.max(3, users) * 9,
    note: "Basic tier (min 3 seats)",
  },
  {
    name: "Trello",
    logo: "📌",
    url: "https://trello.com",
    calculate: (users: number) => users * 5,
    note: "Standard tier",
  },
  {
    name: "Jira",
    logo: "🔧",
    url: "https://www.atlassian.com/software/jira",
    calculate: (users: number) => users <= 10 ? 0 : users * 7.75,
    note: "Standard tier",
  },
  {
    name: "ClickUp",
    logo: "✅",
    url: "https://clickup.com",
    calculate: (users: number) => users * 7,
    note: "Unlimited tier",
  },
  {
    name: "Notion",
    logo: "📝",
    url: "https://notion.so",
    calculate: (users: number) => users * 10,
    note: "Plus tier",
  },
];

export const PMCostCalculator = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSize = Math.min(100, Math.max(5, parseInt(searchParams.get("teamSize") || "10", 10)));
  const [teamSize, setTeamSize] = useState(initialSize);
  const [copied, setCopied] = useState(false);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);

  // Update URL when team size changes
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("teamSize", teamSize.toString());
    setSearchParams(newParams, { replace: true });
  }, [teamSize, searchParams, setSearchParams]);

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/tools?teamSize=${teamSize}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!", {
        description: `Share this link to show costs for ${teamSize} users`,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const calculations = useMemo(() => {
    return toolPricing.map(tool => ({
      ...tool,
      monthlyCost: tool.calculate(teamSize),
      yearlyCost: tool.calculate(teamSize) * 12,
    })).sort((a, b) => a.monthlyCost - b.monthlyCost);
  }, [teamSize]);

  const cheapestTool = calculations[0]?.name;
  const highestCost = Math.max(...calculations.map(t => t.monthlyCost));
  const avgCost = calculations.reduce((sum, t) => sum + t.monthlyCost, 0) / calculations.length;

  const toggleToolSelection = (toolName: string) => {
    setSelectedTools(prev => {
      if (prev.includes(toolName)) {
        return prev.filter(t => t !== toolName);
      }
      if (prev.length >= 3) {
        toast.error("Maximum 3 tools", { description: "Deselect one to add another" });
        return prev;
      }
      return [...prev, toolName];
    });
  };

  const comparedTools = calculations.filter(t => selectedTools.includes(t.name));

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-muted-foreground">
          Compare monthly costs for 7+ project management tools based on your team size. Select up to 3 tools to compare side-by-side.
        </p>
      </div>

      {/* Team Size Selector */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Calculator className="w-4 h-4 text-primary" />
              Team Size
            </label>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-lg px-4 py-1">
                {teamSize} users
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="gap-1.5"
              >
                {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                <span className="hidden sm:inline">{copied ? "Copied!" : "Share"}</span>
              </Button>
            </div>
          </div>
          <Slider
            value={[teamSize]}
            onValueChange={(value) => setTeamSize(value[0])}
            min={5}
            max={100}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>5 users</span>
            <span>50 users</span>
            <span>100 users</span>
          </div>
        </CardContent>
      </Card>

      {/* Compare Selected Button */}
      {selectedTools.length >= 2 && (
        <Button 
          onClick={() => setShowComparison(!showComparison)} 
          className="w-full gap-2"
          variant={showComparison ? "secondary" : "default"}
        >
          <BarChart3 className="w-4 h-4" />
          {showComparison ? "Hide Comparison" : `Compare ${selectedTools.length} Selected Tools`}
        </Button>
      )}

      {/* Side-by-Side Comparison */}
      {showComparison && comparedTools.length >= 2 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4 sm:p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Side-by-Side Comparison
            </h3>
            <div className={`grid gap-4 ${comparedTools.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
              {comparedTools.map((tool, idx) => (
                <div 
                  key={tool.name} 
                  className={`text-center p-4 rounded-lg border ${
                    idx === 0 ? "bg-primary/10 border-primary" : "bg-card border-border"
                  }`}
                >
                  <span className="text-2xl">{tool.logo}</span>
                  <p className="font-semibold mt-2">{tool.name}</p>
                  <p className={`text-2xl font-bold mt-1 ${idx === 0 ? "text-primary" : ""}`}>
                    ${tool.monthlyCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    <span className="text-sm font-normal text-muted-foreground">/mo</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    ${tool.yearlyCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}/year
                  </p>
                  {idx > 0 && (
                    <Badge variant="outline" className="mt-2 text-xs">
                      +${(tool.monthlyCost - comparedTools[0].monthlyCost).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo more
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Savings Highlight */}
      {calculations[0] && calculations[0].monthlyCost < avgCost && (
        <Card className="bg-primary/10 border-primary/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-full bg-primary/20">
              <TrendingDown className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {calculations[0].name} saves vs. average
              </p>
              <p className="text-2xl font-bold text-primary">
                ${((avgCost - calculations[0].monthlyCost) * 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}/year
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cost Comparison */}
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Click to select tools for comparison (max 3)
        </p>
        {calculations.map((tool) => {
          const widthPercent = highestCost > 0 
            ? (tool.monthlyCost / highestCost) * 100 
            : 0;
          const isSelected = selectedTools.includes(tool.name);
          const isCheapest = tool.name === cheapestTool;
          
          return (
            <Card
              key={tool.name}
              onClick={() => toggleToolSelection(tool.name)}
              className={`transition-all cursor-pointer ${
                isSelected 
                  ? "ring-2 ring-primary border-primary bg-primary/5" 
                  : "hover:border-primary/50"
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{tool.logo}</span>
                    <a 
                      href={tool.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className={`font-medium hover:underline ${isCheapest ? "text-primary" : ""}`}
                    >
                      {tool.name}
                    </a>
                    {isSelected && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </div>
                  <div className="text-right">
                    <span className={`text-lg font-bold ${isCheapest ? "text-primary" : ""}`}>
                      ${tool.monthlyCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="text-muted-foreground text-sm">/mo</span>
                  </div>
                </div>
                
                {/* Cost Bar */}
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${
                      isCheapest 
                        ? "bg-primary" 
                        : "bg-muted-foreground/30"
                    }`}
                    style={{ width: `${Math.max(widthPercent, 2)}%` }}
                  />
                </div>
                
                <p className="text-xs text-muted-foreground mt-1.5">
                  {tool.note} • ${(tool.yearlyCost).toLocaleString(undefined, { maximumFractionDigits: 0 })}/year
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* CTA */}
      <div className="pt-4 text-center space-y-3">
        <Button asChild size="lg" className="gap-2">
          <a 
            href="https://www.lottae.com/pricing" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Get Started with Lottae
            <ExternalLink className="w-4 h-4" />
          </a>
        </Button>
        <p className="text-xs text-muted-foreground">
          $7/mo base + $3.50/user • No feature restrictions
        </p>
        
        {/* Link to full article */}
        <div className="pt-2">
          <Link 
            to="/insights/project-management-tools"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <BookOpen className="w-4 h-4" />
            Read the full PM Tools Comparison Guide
          </Link>
        </div>
      </div>
    </div>
  );
};
